import type {
  OpticalMeasureSettings,
  OpticalMeasureSettingsKeys,
} from '../index'

export type SettingLevel = 'dev' | 'debug'

type SettingConfigBase = {
  label: string
  info: string
  level?: SettingLevel
  visibleWhen?: (draft: OpticalMeasureSettings) => boolean
}

type SettingInputConfigBase = SettingConfigBase & {
  key: OpticalMeasureSettingsKeys
}

type BoolSettingConfig = SettingInputConfigBase & {
  type: 'bool'
  fallback: boolean
}

type NumberSettingConfig = SettingInputConfigBase & {
  type: 'number'
  integer?: boolean
}

type SelectSettingConfig = SettingInputConfigBase & {
  type: 'select'
  options: readonly { value: string; label: string }[]
}

export type SettingInputConfig =
  | BoolSettingConfig
  | NumberSettingConfig
  | SelectSettingConfig

export type SettingConfig = SettingInputConfig

export type SectionConfig = {
  id: string
  title: string
  defaultOpen: boolean
  items: readonly SettingConfig[]
}

// Accepts any string path so visibleWhen predicates can use it
// before SettingsPath is derived.
const resolvePath = (settings: OpticalMeasureSettings, path: string): unknown =>
  path.split('.').reduce<unknown>((value, key) => {
    if (value && typeof value === 'object' && key in value) {
      return (value as Record<string, unknown>)[key]
    }
    return undefined
  }, settings)

const usesScaledEyeBox = (d: OpticalMeasureSettings): boolean =>
  (resolvePath(d, 'geometry.eyeBox.useScaled') as boolean | undefined) ?? true

export const SETTINGS_CONFIG = [
  {
    id: 'imageInput',
    title: 'Image Input',
    defaultOpen: false,
    items: [
      {
        key: 'input.pupilDetection',
        type: 'bool',
        fallback: true,
        label: 'Pupil Detection',
        info: 'Uses Vision pupil candidates when available to refine the live PD estimate.',
      },
      {
        key: 'input.screenDimming',
        type: 'bool',
        fallback: true,
        label: 'Dim Screen During SH',
        info: 'Reduces screen brightness during SH measurement to minimize lens reflections from the display. Brightness is restored automatically when done.',
      },
      {
        key: 'input.screenDimmingFactor',
        type: 'number',
        label: 'Dim Factor (0–1)',
        info: 'Screen brightness multiplier during SH mode. New brightness = max(0.1, current × factor). Default 0.333 dims to one third.',
      },
      {
        key: 'input.visionOrientation',
        type: 'select',
        level: 'debug',
        label: 'Orientation',
        info: 'Overrides the Vision image orientation. Leave Auto unless the draw overlay appears rotated or mirrored.',
        options: [
          { value: 'auto', label: 'Auto' },
          { value: 'leftMirrored', label: 'Left mirrored' },
          { value: 'upMirrored', label: 'Up mirrored' },
          { value: 'rightMirrored', label: 'Right mirrored' },
        ],
      },
      {
        key: 'sh.contour.detectionFrameInterval',
        type: 'number',
        integer: true,
        label: 'Detection Frame Interval',
        info: 'Runs SH contour detection every N camera frames. Lower values react faster; higher values reduce Vision work.',
      },
      {
        key: 'sh.contour.maximumContourImageDimension',
        type: 'number',
        integer: true,
        label: 'Contour Max Dimension',
        info: "Caps Vision contour input resolution. Use 0 to keep Apple's default.",
      },
    ],
  },
  {
    id: 'framing',
    title: 'Measurement Framing',
    defaultOpen: true,
    items: [
      {
        key: 'framing.normal.preferredDistanceCm',
        type: 'number',
        level: 'dev',
        label: 'Normal Preferred Distance (cm)',
        info: 'Explicit target distance for PD and SH without a VBOX reference.',
      },
      {
        key: 'framing.normal.guidanceDistanceToleranceCm',
        type: 'number',
        level: 'dev',
        label: 'Normal Guidance Tolerance (cm)',
        info: 'Allowed distance on either side of the normal target before user guidance changes.',
      },
      {
        key: 'framing.normal.captureDistanceToleranceCm',
        type: 'number',
        level: 'dev',
        label: 'Normal Capture Tolerance (cm)',
        info: 'Native SH safety margin on either side of the normal target.',
      },
      {
        key: 'framing.vbox.preferredDistanceCm',
        type: 'number',
        level: 'dev',
        label: 'VBOX Preferred Distance (cm)',
        info: 'Explicit target distance for SH with a VBOX reference.',
      },
      {
        key: 'framing.vbox.guidanceDistanceToleranceCm',
        type: 'number',
        level: 'dev',
        label: 'VBOX Guidance Tolerance (cm)',
        info: 'Allowed distance on either side of the VBOX target before user guidance changes.',
      },
      {
        key: 'framing.vbox.captureDistanceToleranceCm',
        type: 'number',
        level: 'dev',
        label: 'VBOX Capture Tolerance (cm)',
        info: 'Native SH safety margin on either side of the VBOX target.',
      },
      {
        key: 'framing.targetCenter.x',
        type: 'number',
        level: 'debug',
        label: 'Target Center X',
        info: 'Horizontal target center in normalized camera coordinates.',
      },
      {
        key: 'framing.targetCenter.y',
        type: 'number',
        level: 'debug',
        label: 'Target Center Y',
        info: 'Vertical target center in normalized camera coordinates.',
      },
      {
        key: 'framing.faceFit.mode',
        type: 'select',
        level: 'dev',
        label: 'Face Fit Mode',
        info: 'Session Calibrated can resolve one bounded user-specific guide. Fallback always uses the canonical guide.',
        options: [
          { value: 'session-calibrated', label: 'Session Calibrated' },
          { value: 'fallback', label: 'Fallback' },
        ],
      },
      {
        key: 'framing.faceFit.fallbackFaceWidthMm',
        type: 'number',
        level: 'debug',
        label: 'Fallback Face Width (mm)',
        info: 'Provisional canonical physical width used to project the fallback guide.',
      },
      {
        key: 'framing.faceFit.fallbackFaceHeightMm',
        type: 'number',
        level: 'debug',
        label: 'Fallback Face Height (mm)',
        info: 'Provisional canonical physical height used to project the fallback guide.',
      },
      {
        key: 'framing.faceFit.ovalPaddingFactor',
        type: 'number',
        level: 'debug',
        label: 'Oval Padding Factor',
        info: 'Padding applied when fitting the oval around projected face dimensions.',
      },
      {
        key: 'framing.faceFit.minScaleFactor',
        type: 'number',
        level: 'debug',
        label: 'Minimum Calibrated Scale',
        info: 'Lower clamp for session-calibrated guide size relative to fallback.',
      },
      {
        key: 'framing.faceFit.maxScaleFactor',
        type: 'number',
        level: 'debug',
        label: 'Maximum Calibrated Scale',
        info: 'Upper clamp for session-calibrated guide size relative to fallback.',
      },
      {
        key: 'framing.faceFit.stableFrameCount',
        type: 'number',
        integer: true,
        level: 'debug',
        label: 'Calibration Stable Frames',
        info: 'Number of valid face frames required before session calibration locks.',
      },
      {
        key: 'framing.faceFit.timeoutMs',
        type: 'number',
        integer: true,
        level: 'debug',
        label: 'Calibration Timeout (ms)',
        info: 'Time before unresolved session calibration settles on fallback.',
      },
      {
        key: 'framing.position.guidanceToleranceXFactor',
        type: 'number',
        level: 'debug',
        label: 'Guidance Horizontal Tolerance',
        info: 'Allowed horizontal center offset relative to oval half-width.',
      },
      {
        key: 'framing.position.guidanceToleranceYFactor',
        type: 'number',
        level: 'debug',
        label: 'Guidance Vertical Tolerance',
        info: 'Allowed vertical center offset relative to oval half-height.',
      },
      {
        key: 'framing.position.captureToleranceXFactor',
        type: 'number',
        level: 'debug',
        label: 'Capture Horizontal Tolerance',
        info: 'Native capture horizontal tolerance; must not be tighter than guidance.',
      },
      {
        key: 'framing.position.captureToleranceYFactor',
        type: 'number',
        level: 'debug',
        label: 'Capture Vertical Tolerance',
        info: 'Native capture vertical tolerance; must not be tighter than guidance.',
      },
      {
        key: 'framing.position.guidanceSizeToleranceFactor',
        type: 'number',
        level: 'debug',
        label: 'Guidance Size Tolerance',
        info: 'Allowed live face-size error before user guidance changes.',
      },
      {
        key: 'framing.position.captureSizeToleranceFactor',
        type: 'number',
        level: 'debug',
        label: 'Capture Size Tolerance',
        info: 'Native capture size tolerance; must not be tighter than guidance.',
      },
      {
        key: 'framing.enforcement.guidanceEnabled',
        type: 'bool',
        fallback: false,
        level: 'debug',
        label: 'Framing Guidance Enforcement',
        info: 'Reserved for the validated guidance gate. Disabled during passive integration.',
      },
      {
        key: 'framing.enforcement.captureEnabled',
        type: 'bool',
        fallback: false,
        level: 'debug',
        label: 'Framing Capture Enforcement',
        info: 'Reserved for the validated native capture gate. Disabled during passive integration.',
      },
    ],
  },
  {
    id: 'coreml',
    title: 'Core ML',
    defaultOpen: true,
    items: [
      {
        key: 'sh.source',
        type: 'select',
        level: 'dev',
        label: 'SH Source',
        info: 'Vision: standard edge-detection on the live frame. Core ML: runs lens segmentation on each frame instead.',
        options: [
          { value: 'vision', label: 'Vision' },
          { value: 'coreml', label: 'Core ML' },
        ],
      },
      {
        key: 'sh.coreml.modelVariant',
        type: 'select',
        level: 'dev',
        label: 'Live Model',
        info: 'Core ML model used on normal live SH frames.',
        options: [
          { value: 'small_512', label: 'Small 512' },
          { value: 'medium_512', label: 'Medium 512' },
        ],
        visibleWhen: (d) => d.sh.source === 'coreml',
      },
      {
        key: 'sh.coreml.finalFrameModelVariant',
        type: 'select',
        level: 'dev',
        label: 'Final Frame Model',
        info: 'Core ML model used for the frozen best-frame rerun.',
        options: [
          { value: 'small_512', label: 'Small 512' },
          { value: 'medium_512', label: 'Medium 512' },
        ],
        visibleWhen: (d) => d.sh.source === 'coreml',
      },
      {
        key: 'sh.coreml.cropPaddingXFactorCap',
        type: 'number',
        level: 'dev',
        label: 'Final ROI X Cap',
        info: 'Maximum horizontal scaled ROI padding for the final Core ML rerun.',
        visibleWhen: (d) => d.sh.source === 'coreml',
      },
      {
        key: 'sh.coreml.cropPaddingTopFactorCap',
        type: 'number',
        level: 'dev',
        label: 'Final ROI Top Cap',
        info: 'Maximum top scaled ROI padding for the final Core ML rerun.',
        visibleWhen: (d) => d.sh.source === 'coreml',
      },
      {
        key: 'sh.coreml.cropPaddingBottomFactorCap',
        type: 'number',
        level: 'dev',
        label: 'Final ROI Bottom Cap',
        info: 'Maximum bottom scaled ROI padding for the final Core ML rerun.',
        visibleWhen: (d) => d.sh.source === 'coreml',
      },
      {
        key: 'sh.coreml.frameInterval',
        type: 'number',
        integer: true,
        level: 'dev',
        label: 'Core ML Frame Interval',
        info: 'Runs Core ML contour detection every N frames. Only active when SH Source is Core ML.',
        visibleWhen: (d) => d.sh.source === 'coreml',
      },
      {
        key: 'sh.coreml.cropTopExpansionFactor',
        type: 'number',
        level: 'debug',
        label: 'Crop Top Expansion',
        info: 'Extra crop height above the eye box as a fraction of box height, shared by the glasses-presence and Core ML crops.',
        visibleWhen: (d) => d.sh.source === 'coreml',
      },
      {
        key: 'sh.coreml.maskThreshold',
        type: 'number',
        level: 'dev',
        label: 'Mask Threshold',
        info: 'Binarization threshold (0–1) applied to the Core ML lens probability mask before contour extraction.',
        visibleWhen: (d) => d.sh.source === 'coreml',
      },
      {
        key: 'sh.coreml.subpixelRefinement',
        type: 'bool',
        fallback: true,
        level: 'dev',
        label: 'Sub-Pixel Mask Edges',
        info: 'Refines mask contour points to the sub-pixel threshold crossing in each column. Removes the ~1 px quantization step on the lens bottom/top edges.',
        visibleWhen: (d) => d.sh.source === 'coreml',
      },
    ],
  },
  {
    id: 'geometry',
    title: 'Detection Area',
    defaultOpen: false,
    items: [
      {
        key: 'geometry.corneaProjectionOffset',
        type: 'number',
        label: 'Cornea Offset (m)',
        info: 'Distance projected from the eye transform toward the cornea. This tunes where PD is measured relative to the tracked eye.',
      },
      {
        key: 'geometry.rotationXOffsetDeg',
        type: 'number',
        label: 'Pitch Offset (deg)',
        info: 'Global calibration offset added to the ARKit head pitch. Applied at the source, so gates, quality scoring, tilt correction, and guidance prompts all see the adjusted value.',
      },
      {
        key: 'geometry.eyeBox.useScaled',
        type: 'bool',
        fallback: true,
        label: 'Scale ROI by Distance',
        info: 'Scales the detection area from live eye distance so ROI size follows how far the customer is from the screen.',
      },
      {
        key: 'geometry.eyeBox.paddingXFactor',
        type: 'number',
        label: 'ROI X Factor',
        info: 'Horizontal ROI margin as a fraction of eye distance. Increase it if frame sides are clipped.',
        visibleWhen: usesScaledEyeBox,
      },
      {
        key: 'geometry.eyeBox.paddingTopFactor',
        type: 'number',
        label: 'ROI Top Factor',
        info: 'Top ROI margin as a fraction of eye distance. Lower values reject more eyebrow and eyelid contours.',
        visibleWhen: usesScaledEyeBox,
      },
      {
        key: 'geometry.eyeBox.paddingBottomFactor',
        type: 'number',
        label: 'ROI Bottom Factor',
        info: 'Bottom ROI margin as a fraction of eye distance. Increase it if lower frame rims are clipped.',
        visibleWhen: usesScaledEyeBox,
      },
      {
        key: 'geometry.eyeBox.paddingX',
        type: 'number',
        integer: true,
        label: 'Eye ROI Margin X',
        info: 'Fixed horizontal margin around the eye region used for rim detection.',
        visibleWhen: (d) => !usesScaledEyeBox(d),
      },
      {
        key: 'geometry.eyeBox.paddingY',
        type: 'number',
        integer: true,
        label: 'Eye ROI Margin Y',
        info: 'Fixed vertical margin around the eye region used for rim detection.',
        visibleWhen: (d) => !usesScaledEyeBox(d),
      },
      {
        key: 'geometry.eyeBox.paddingXMinPx',
        type: 'number',
        level: 'debug',
        integer: true,
        label: 'ROI X Min (px)',
        info: 'Pixel floor for the scaled horizontal ROI margin.',
        visibleWhen: usesScaledEyeBox,
      },
      {
        key: 'geometry.eyeBox.paddingTopMinPx',
        type: 'number',
        level: 'debug',
        integer: true,
        label: 'ROI Top Min (px)',
        info: 'Pixel floor for the scaled top ROI margin.',
        visibleWhen: usesScaledEyeBox,
      },
      {
        key: 'geometry.eyeBox.paddingBottomMinPx',
        type: 'number',
        level: 'debug',
        integer: true,
        label: 'ROI Bottom Min (px)',
        info: 'Pixel floor for the scaled bottom ROI margin.',
        visibleWhen: usesScaledEyeBox,
      },
    ],
  },
  {
    id: 'review',
    title: 'Review',
    defaultOpen: true,
    items: [
      {
        key: 'sh.review.enabled',
        type: 'bool',
        fallback: true,
        label: 'Review Zoom',
        info: 'Automatically zooms frozen PD and SH review frames around the accepted detection area.',
      },
      {
        key: 'sh.review.zoomScale',
        type: 'number',
        label: 'Zoom Scale',
        info: 'Target zoom scale for frozen PD and SH review. Native clamps this to a conservative range.',
      },
      {
        key: 'sh.review.paddingFactor',
        type: 'number',
        label: 'Review Padding',
        info: 'Extra breathing room around the frozen PD or SH detection area before review zoom is applied.',
      },
      {
        key: 'sh.review.animationMs',
        type: 'number',
        integer: true,
        label: 'Review Animation',
        info: 'Native transition duration in milliseconds. Use 0 for an immediate review frame.',
      },
      {
        key: 'sh.review.verticalOffsetFactor',
        type: 'number',
        label: 'Vertical Offset',
        info: 'Fraction of view height to shift the zoom focus vertically. Negative moves the focus up, away from bottom overlays.',
      },
    ],
  },
  {
    id: 'rim',
    title: 'Frame Rim Detection',
    defaultOpen: false,
    items: [
      {
        key: 'sh.contour.contrastAdjustment',
        type: 'number',
        label: 'Contrast (0-3)',
        info: 'Image contrast adjustment applied before contour detection. Higher values can help faint rims but may add noise.',
      },
      {
        key: 'sh.contour.detectsDarkOnLight',
        type: 'bool',
        fallback: true,
        label: 'Dark on Light (Glasses)',
        info: 'Detects dark frame rims against a lighter face/background. Turn off for opposite contrast setups.',
      },
      {
        key: 'sh.contour.minContourPoints',
        type: 'number',
        level: 'debug',
        integer: true,
        label: 'Min Points',
        info: 'Minimum number of contour points required before a rim candidate is accepted.',
      },
      {
        key: 'sh.contour.minContourArea',
        type: 'number',
        integer: true,
        label: 'Min Area (px²)',
        info: 'Minimum pixel area for accepted rim contours. Increase it to reject tiny noise; decrease it for small or thin frames.',
      },
      {
        key: 'sh.contour.boxEdgeTolerance',
        type: 'number',
        level: 'debug',
        label: 'ROI Edge Tolerance',
        info: 'Rejects contours that sit within this many pixels of the ROI edge, which are usually clipped by the detection box.',
      },
      {
        key: 'sh.contour.rimProximityFactor',
        type: 'number',
        label: 'Rim Proximity',
        info: 'How close a contour must be to the expected rim area. Lower values are stricter; higher values accept farther candidates.',
      },
      {
        key: 'sh.contour.minDownwardOffsetFactor',
        type: 'number',
        level: 'debug',
        label: 'Min Downward Offset',
        info: 'Minimum downward distance from pupil as a fraction of eye separation before a rim point can be used.',
      },
      {
        key: 'sh.contour.rawFallbackBandFactor',
        type: 'number',
        level: 'debug',
        label: 'Raw Fallback Band',
        info: 'Expands the raw bottom-point search band when no points are found near the pupil column.',
      },
      {
        key: 'sh.contour.rawLateralPenalty',
        type: 'number',
        level: 'debug',
        label: 'Raw Lateral Penalty',
        info: 'Penalty applied to raw bottom points that sit sideways from the pupil column.',
      },
      {
        key: 'sh.contour.rawBottomPointFraction',
        type: 'number',
        level: 'debug',
        label: 'Raw Bottom Fraction',
        info: 'Fraction of the lowest raw contour points averaged into the raw SH bottom point.',
      },
      {
        key: 'sh.contour.rejectBrightContours',
        type: 'bool',
        fallback: true,
        label: 'Reject Bright Reflections',
        info: 'Rejects bright, smooth nested contours that usually come from iPad screen reflections in the lens.',
      },
      {
        key: 'sh.contour.brightContourLumaThreshold',
        type: 'number',
        label: 'Bright Luma Threshold',
        info: 'Average luma threshold for bright-contour rejection. Higher values are less aggressive.',
      },
      {
        key: 'sh.contour.brightReflectionSmoothnessThreshold',
        type: 'number',
        level: 'debug',
        label: 'Reflection Smoothness',
        info: 'Minimum smoothness required before a bright contour is treated as reflection-like.',
      },
      {
        key: 'sh.contour.brightReflectionAspectRatioThreshold',
        type: 'number',
        level: 'debug',
        label: 'Reflection Aspect Ratio',
        info: 'Wide bright contours above this aspect ratio can be rejected as display reflections.',
      },
      {
        key: 'sh.contour.lumaSampleBoxFactor',
        type: 'number',
        level: 'debug',
        label: 'Luma Sample Box',
        info: 'Fraction of contour bounding box sampled for interior luma during bright-reflection checks.',
      },
      {
        key: 'sh.lowerArcFit.enabled',
        type: 'bool',
        fallback: false,
        label: 'Use Rim Arc Fit',
        info: 'Fits rim arcs (bottom and boxing top) at the pupil column instead of using raw extreme contour points. Applies to both Vision and Core ML sources.',
      },
      {
        key: 'sh.lowerArcFit.minPoints',
        type: 'number',
        integer: true,
        label: 'Arc Fit Min Points',
        info: 'Minimum lower-rim points needed before the fitted curve can be used.',
      },
      {
        key: 'sh.lowerArcFit.bandFactor',
        type: 'number',
        label: 'Arc Fit Band',
        info: 'Horizontal fitting band as a multiplier of rim proximity.',
      },
      {
        key: 'sh.lowerArcFit.residualFactor',
        type: 'number',
        label: 'Arc Fit Residual',
        info: 'Maximum allowed curve residual as a fraction of eye separation.',
      },
      {
        key: 'sh.lowerArcFit.fitPointFraction',
        type: 'number',
        level: 'debug',
        label: 'Arc Fit Point Fraction',
        info: 'Fraction of lower-rim candidate points used for the quadratic arc fit.',
      },
    ],
  },
  {
    id: 'captureAndGates',
    title: 'SH Capture and Gates',
    defaultOpen: false,
    items: [
      {
        key: 'sh.contour.contourSmoothingRadius',
        type: 'number',
        integer: true,
        label: 'Contour Smooth Radius',
        info: 'Spatial smoothing radius for detected contours. Higher values reduce jagged edges but can soften detail.',
      },
      {
        key: 'sh.contour.contourOutlierFactor',
        type: 'number',
        label: 'Contour Outlier Factor',
        info: 'Removes isolated spike points before smoothing. Lower values are stricter; use 0 to disable.',
      },
      {
        key: 'sh.contour.outlierMinDeviationPx',
        type: 'number',
        level: 'debug',
        label: 'Outlier Min Deviation (px)',
        info: 'Pixel floor for the spike-outlier deviation threshold used by Vision contour cleanup.',
      },
      {
        key: 'sh.smoothing.rimSmoothing',
        type: 'number',
        level: 'debug',
        label: 'Rim EMA (0-1)',
        info: 'Vision-source spatial continuity hint only (preferred rim position). Does not smooth SH values or drawn gauges.',
      },
      {
        key: 'sh.smoothing.persistenceFrames',
        type: 'number',
        level: 'debug',
        integer: true,
        label: 'SH Persistence Frames',
        info: 'Detection frames to hold the last valid SH value after contour loss.',
      },
      {
        key: 'sh.capture.eyeOpenThreshold',
        type: 'number',
        label: 'SH Eye Open Threshold',
        info: 'Minimum eye-openness score accepted during SH capture.',
      },
      {
        key: 'sh.capture.targetCandidateFrames',
        type: 'number',
        integer: true,
        label: 'Target SH Frames',
        info: 'Number of accepted SH candidate frames needed before the capture completes.',
      },
      {
        key: 'sh.capture.multiFrameMedianEnabled',
        type: 'bool',
        fallback: true,
        label: 'Multi-Frame Median',
        info: 'Reports the median SH of all accepted capture frames, reprojected onto the frozen frame. Off reverts to the frozen frame’s single detection.',
      },
      {
        key: 'sh.capture.medianQualityWeightExponent',
        type: 'number',
        label: 'Median Quality Exponent',
        info: 'Weighted median: each sample weighs quality^exponent, so better frames pull the result harder. Use 0 for a plain unweighted median.',
        visibleWhen: (d) => d.sh.capture.multiFrameMedianEnabled,
      },
      {
        key: 'sh.capture.finalFrameMedianWeight',
        type: 'number',
        label: 'Final Frame Weight',
        info: 'Weight multiplier for the frozen final detection when it joins the median. Use 0 to exclude the final frame from the median.',
        visibleWhen: (d) => d.sh.capture.multiFrameMedianEnabled,
      },
      {
        key: 'sh.capture.maxSampleSpreadMm',
        type: 'number',
        label: 'Max Sample Spread (mm)',
        info: 'Soft warning threshold for the spread (max − min) of accepted SH samples. Exceeding it only flags highSpread; the capture still completes.',
        visibleWhen: (d) => d.sh.capture.multiFrameMedianEnabled,
      },
      {
        key: 'sh.correction.pantoscopicTiltDeg',
        type: 'number',
        label: 'Pantoscopic Tilt (deg)',
        info: 'Assumed lens pantoscopic tilt used to correct SH by 1/cos(tilt + head pitch). Use 0 to disable the correction.',
      },
      {
        key: 'sh.correction.includeHeadPitch',
        type: 'bool',
        fallback: true,
        label: 'Tilt Includes Head Pitch',
        info: 'Adds the measured head pitch to the pantoscopic tilt angle in the SH correction.',
        visibleWhen: (d) => d.sh.correction.pantoscopicTiltDeg !== 0,
      },
      {
        key: 'sh.correction.maxCorrectionFactor',
        type: 'number',
        level: 'debug',
        label: 'Max Tilt Correction',
        info: 'Upper bound for the tilt correction multiplier.',
        visibleWhen: (d) => d.sh.correction.pantoscopicTiltDeg !== 0,
      },
      {
        key: 'sh.gates.maxDifferenceMm',
        type: 'number',
        label: 'Max SH Delta (mm)',
        info: 'Rejects SH frames when left and right segment heights differ by more than this.',
      },
      {
        key: 'sh.gates.glassesPresenceMinConfidence',
        type: 'number',
        label: 'Glasses Confidence',
        info: 'Minimum classifier probability required before SH detection continues. Use 0 to disable this gate.',
      },
      {
        key: 'sh.gates.eyeOpenMin',
        type: 'number',
        label: 'Native Eye Open Min',
        info: 'Native hard gate: minimum eye openness for a frame to count as an SH candidate. Looser than the JS guidance threshold by design.',
      },
      {
        key: 'sh.gates.maxRotationXDeg',
        type: 'number',
        label: 'Native Max Pitch (deg)',
        info: 'Native hard gate: maximum head pitch for candidate counting. Looser than the JS guidance threshold by design.',
      },
      {
        key: 'sh.gates.maxRotationYDeg',
        type: 'number',
        label: 'Native Max Yaw (deg)',
        info: 'Native hard gate: maximum head yaw for candidate counting. Looser than the JS guidance threshold by design.',
      },
      {
        key: 'sh.scale.trustRatioDeviation',
        type: 'number',
        label: 'Scale Trust Band',
        info: 'VBOX-vs-PD scale disagreement the VBOX scale is still trusted in full up to. Past it the VBOX scale fades toward PD instead of switching outright.',
      },
      {
        key: 'sh.scale.maxScaleRatioDeviation',
        type: 'number',
        label: 'Scale Ratio Tolerance',
        info: 'VBOX-vs-PD scale disagreement at which the VBOX scale is fully replaced by the PD scale. Wide by default so PD only catches plainly broken detections. Use 0 to disable the gate.',
      },
      {
        key: 'sh.estimate.formula',
        type: 'select',
        level: 'dev',
        label: 'SH Estimate Formula',
        info: 'Optional VBOX-based expected-SH prior used in quality scoring. Pilot uses two thirds of VBOX; Standard uses half VBOX plus 2 mm.',
        options: [
          { value: 'standard', label: 'Standard' },
          { value: 'pilot', label: 'Pilot' },
          { value: 'off', label: 'Off' },
        ],
      },
      {
        key: 'sh.estimate.toleranceMm',
        type: 'number',
        level: 'dev',
        label: 'SH Estimate Tolerance',
        info: 'Allowed difference from the VBOX-based expected SH before the estimate quality score reaches zero.',
        visibleWhen: (d) => d.sh.estimate.formula !== 'off',
      },
      {
        key: 'sh.estimate.scoreExponent',
        type: 'number',
        level: 'dev',
        label: 'SH Estimate Score Curve',
        info: 'Exponent applied to the estimate score falloff. Higher values are more forgiving near the target and harsher near tolerance.',
        visibleWhen: (d) => d.sh.estimate.formula !== 'off',
      },
      {
        key: 'sh.gates.plausibleMinMm',
        type: 'number',
        label: 'Min SH (mm)',
        info: 'Rejects segment-height candidates below this physical value.',
      },
      {
        key: 'sh.gates.plausibleMaxMm',
        type: 'number',
        label: 'Max SH (mm)',
        info: 'Rejects segment-height candidates above this physical value.',
      },
      {
        key: 'sh.sharedContourCorrection.factor',
        type: 'number',
        label: 'Shared SH Correction Factor',
        info: 'Reduces SH for shared/full-frame contours by this fraction of contour height.',
      },
      {
        key: 'sh.sharedContourCorrection.maxMm',
        type: 'number',
        label: 'Shared SH Max Correction (mm)',
        info: 'Caps the shared/full-frame contour SH correction.',
      },
      {
        key: 'sh.calibration.sweepCount',
        type: 'number',
        integer: true,
        label: 'Calibration Samples',
        info: 'Number of contrast candidates tested during calibration.',
      },
      {
        key: 'sh.calibration.sweepStep',
        type: 'number',
        label: 'Calibration Step',
        info: 'Contrast increment used between calibration candidates.',
      },
      {
        key: 'sh.calibration.confidenceDropRecalibrationEnabled',
        type: 'bool',
        fallback: true,
        label: 'Confidence Recalibration',
        info: 'Runs a narrow contrast sweep when a previously locked SH candidate loses confidence.',
      },
      {
        key: 'sh.calibration.minCandidateConfidence',
        type: 'number',
        label: 'Min Candidate Confidence',
        info: 'Native recalibration can trigger below this contour confidence after a good SH lock.',
      },
      {
        key: 'sh.calibration.confidenceDropRecalibrationFrames',
        type: 'number',
        integer: true,
        label: 'Low Confidence Frames',
        info: 'Number of low-confidence contour frames before native recalibration.',
      },
      {
        key: 'sh.calibration.confidenceDropMaxRecalibrations',
        type: 'number',
        level: 'debug',
        integer: true,
        label: 'Max Recalibrations',
        info: 'Maximum confidence-drop recalibrations in one SH session.',
      },
      {
        key: 'sh.calibration.narrowSweepCount',
        type: 'number',
        level: 'debug',
        integer: true,
        label: 'Narrow Sweep Samples',
        info: 'Number of contrast candidates used for confidence-drop recalibration.',
      },
      {
        key: 'sh.calibration.narrowSweepStep',
        type: 'number',
        level: 'debug',
        label: 'Narrow Sweep Step',
        info: 'Contrast spacing for confidence-drop recalibration.',
      },
    ],
  },
  {
    id: 'hsaPoseGate',
    title: 'HSA Pose Gate',
    defaultOpen: false,
    items: [
      {
        key: 'hsa.pose.minSideRotationY',
        type: 'number',
        label: 'HSA Min Side Rotation',
        info: 'Minimum absolute Y rotation, in degrees, required before HSA treats the pose as side-profile eligible.',
      },
      {
        key: 'hsa.pose.maxSideRotationY',
        type: 'number',
        label: 'HSA Max Side Rotation',
        info: 'Maximum absolute Y rotation, in degrees, accepted before asking the user to turn slightly back toward the camera.',
      },
      {
        key: 'hsa.pose.maxPitchRotationX',
        type: 'number',
        label: 'HSA Max Pitch',
        info: 'Maximum absolute X rotation, in degrees, accepted for the HSA side-profile gate.',
      },
      {
        key: 'hsa.detector.freezeSideFrameOnPose',
        type: 'bool',
        fallback: false,
        label: 'Freeze HSA Side Frame',
        info: 'Freezes the first good-enough HSA side-pose frame for detector debugging before lens geometry is accepted.',
      },
    ],
  },
  {
    id: 'hsaRoi',
    title: 'HSA ROI',
    defaultOpen: false,
    items: [
      {
        key: 'hsa.detector.roiWidthFactor',
        type: 'number',
        label: 'HSA ROI Width Factor',
        info: 'Side-profile detector ROI width as a multiple of projected eye distance.',
      },
      {
        key: 'hsa.detector.roiHeightFactor',
        type: 'number',
        label: 'HSA ROI Height Factor',
        info: 'Side-profile detector ROI height as a multiple of projected eye distance.',
      },
      {
        key: 'hsa.detector.roiCenterXOffsetFactor',
        type: 'number',
        label: 'HSA ROI X Offset',
        info: 'Horizontal ROI center offset as a multiple of projected eye distance, mirrored by side-pose direction.',
      },
      {
        key: 'hsa.detector.roiCenterYOffsetFactor',
        type: 'number',
        label: 'HSA ROI Y Offset',
        info: 'Vertical ROI center offset as a multiple of projected eye distance.',
      },
      {
        key: 'hsa.detector.boxEdgeTolerance',
        type: 'number',
        label: 'HSA ROI Edge Tolerance',
        info: 'Rejects HSA contours that touch the side-profile ROI boundary within this many pixels.',
      },
    ],
  },
  {
    id: 'hsaContourDetection',
    title: 'HSA Contour Detection',
    defaultOpen: false,
    items: [
      {
        key: 'hsa.detector.detectionFrameInterval',
        type: 'number',
        integer: true,
        label: 'HSA Detection Frame Interval',
        info: 'Runs HSA side-profile contour detection every N camera frames. Frozen-frame reruns ignore this throttle.',
      },
      {
        key: 'hsa.detector.contrastAdjustment',
        type: 'number',
        label: 'HSA Contrast',
        info: 'Vision contour contrast adjustment used by the HSA side-profile detector.',
      },
      {
        key: 'hsa.detector.detectsDarkOnLight',
        type: 'bool',
        fallback: true,
        label: 'HSA Dark on Light',
        info: 'Controls Vision contour polarity for the HSA side-profile detector.',
      },
      {
        key: 'hsa.detector.maximumContourImageDimension',
        type: 'number',
        integer: true,
        label: 'HSA Contour Max Dimension',
        info: "Caps Vision contour input resolution for HSA. Use 0 to keep Apple's default.",
      },
      {
        key: 'hsa.detector.minContourPoints',
        type: 'number',
        integer: true,
        label: 'HSA Min Contour Points',
        info: 'Rejects HSA side-profile contours with fewer points.',
      },
      {
        key: 'hsa.detector.minContourArea',
        type: 'number',
        label: 'HSA Min Contour Area',
        info: 'Rejects HSA side-profile contours with a small screen-space bounding area.',
      },
    ],
  },
  {
    id: 'hsaCandidateAcceptance',
    title: 'HSA Candidate Acceptance',
    defaultOpen: false,
    items: [
      {
        key: 'hsa.detector.candidateMinConfidence',
        type: 'number',
        label: 'HSA Candidate Confidence',
        info: 'Minimum confidence required before HSA treats side-profile lens geometry as selected.',
      },
    ],
  },
  {
    id: 'visionScoring',
    title: 'Vision Scoring',
    defaultOpen: false,
    items: [
      {
        key: 'sh.scoring.jaggednessWeight',
        type: 'number',
        label: 'Jaggedness Weight',
        info: 'Calibration scoring weight for jagged contour edges. Higher values prefer smoother-looking rims.',
      },
      {
        key: 'sh.scoring.convexityWeight',
        type: 'number',
        label: 'Convexity Weight',
        info: 'Calibration scoring weight for contour solidity. Higher values prefer contours closer to their convex hull.',
      },
      {
        key: 'sh.scoring.compactnessWeight',
        type: 'number',
        label: 'Compactness Weight',
        info: 'Candidate scoring weight for compact contour shape.',
      },
      {
        key: 'sh.scoring.shapeScale',
        type: 'number',
        level: 'debug',
        label: 'Shape Score Scale',
        info: 'Overall scale applied to smoothness, solidity, and compactness in candidate scoring.',
      },
      {
        key: 'sh.scoring.continuityWeight',
        type: 'number',
        level: 'debug',
        label: 'Continuity Weight',
        info: 'Score bonus for contours near the previously accepted rim.',
      },
      {
        key: 'sh.scoring.candidateHysteresis',
        type: 'number',
        level: 'debug',
        label: 'Candidate Hysteresis',
        info: 'Score multiple a challenger needs before replacing the current candidate.',
      },
      {
        key: 'sh.scoring.contourHierarchyDepthPenalty',
        type: 'number',
        level: 'debug',
        label: 'Hierarchy Penalty',
        info: 'Score multiplier per Vision contour nesting level. Lower values favour parent glasses/lens blobs over nested inner contours.',
      },
      {
        key: 'sh.scoring.aspectRatioBonusThreshold',
        type: 'number',
        level: 'debug',
        label: 'Aspect Bonus Threshold',
        info: 'Aspect ratio where wide, shallow contours start receiving a score bonus.',
      },
      {
        key: 'sh.scoring.aspectRatioBonusSlope',
        type: 'number',
        level: 'debug',
        label: 'Aspect Bonus Slope',
        info: 'How quickly the aspect-ratio score bonus grows above the threshold.',
      },
      {
        key: 'sh.scoring.maxAspectBonus',
        type: 'number',
        level: 'debug',
        label: 'Max Aspect Bonus',
        info: 'Maximum extra score from aspect-ratio evidence.',
      },
      {
        key: 'sh.scoring.lowAspectPenalty',
        type: 'number',
        level: 'debug',
        label: 'Low Aspect Penalty',
        info: 'Score multiplier for contours that are not wide enough to look rim-like.',
      },
      {
        key: 'sh.scoring.spanTargetMin',
        type: 'number',
        level: 'debug',
        label: 'Span Target Min',
        info: 'Minimum contour span, as eye-separation fraction, for the good-span bonus.',
      },
      {
        key: 'sh.scoring.spanTargetMax',
        type: 'number',
        level: 'debug',
        label: 'Span Target Max',
        info: 'Maximum contour span, as eye-separation fraction, for the good-span bonus.',
      },
      {
        key: 'sh.scoring.spanTooSmall',
        type: 'number',
        level: 'debug',
        label: 'Span Too Small',
        info: 'Contours below this eye-separation fraction receive the narrow-span penalty.',
      },
      {
        key: 'sh.scoring.goodSpanBonus',
        type: 'number',
        level: 'debug',
        label: 'Good Span Bonus',
        info: 'Score multiplier for contours in the target span range.',
      },
      {
        key: 'sh.scoring.narrowSpanPenalty',
        type: 'number',
        level: 'debug',
        label: 'Narrow Span Penalty',
        info: 'Score multiplier for contours that are too narrow.',
      },
      {
        key: 'sh.scoring.wideSpanPenalty',
        type: 'number',
        level: 'debug',
        label: 'Wide Span Penalty',
        info: 'Score multiplier for contours that are too wide.',
      },
      {
        key: 'sh.scoring.distanceMinSeparationFactor',
        type: 'number',
        level: 'debug',
        label: 'Distance Floor Factor',
        info: 'Minimum center-distance denominator as a fraction of eye separation.',
      },
      {
        key: 'sh.scoring.downwardWeight',
        type: 'number',
        level: 'debug',
        label: 'Downward Weight',
        info: 'Score growth from how far the candidate bottom point sits below the pupil.',
      },
      {
        key: 'sh.scoring.downwardMaxBonus',
        type: 'number',
        level: 'debug',
        label: 'Downward Max Bonus',
        info: 'Maximum score bonus from downward evidence.',
      },
      {
        key: 'sh.scoring.lateralMinFactor',
        type: 'number',
        level: 'debug',
        label: 'Lateral Min Factor',
        info: 'Minimum score multiplier allowed for sideways rim points.',
      },
      {
        key: 'sh.scoring.lateralPenalty',
        type: 'number',
        level: 'debug',
        label: 'Lateral Penalty',
        info: 'How strongly sideways rim points are penalized.',
      },
      {
        key: 'sh.scoring.partialConfidenceFactor',
        type: 'number',
        level: 'debug',
        label: 'Partial Confidence Factor',
        info: 'Confidence multiplier when only one eye has a rim candidate.',
      },
      {
        key: 'sh.scoring.symmetryToleranceFactor',
        type: 'number',
        level: 'debug',
        label: 'Symmetry Tolerance',
        info: 'Left/right confidence symmetry tolerance as a fraction of eye separation.',
      },
      {
        key: 'sh.scoring.pairConfidenceWeight',
        type: 'number',
        level: 'debug',
        label: 'Pair Confidence Weight',
        info: 'Weight of the weaker per-eye confidence in pair confidence.',
      },
      {
        key: 'sh.scoring.symmetryConfidenceWeight',
        type: 'number',
        level: 'debug',
        label: 'Symmetry Confidence Weight',
        info: 'Weight of left/right symmetry in pair confidence.',
      },
      {
        key: 'sh.scoring.scoreConfidenceLogDivisor',
        type: 'number',
        level: 'debug',
        label: 'Score Confidence Divisor',
        info: 'Log scaling divisor used when converting candidate score to confidence.',
      },
      {
        key: 'sh.scoring.confidenceScoreWeight',
        type: 'number',
        level: 'debug',
        label: 'Confidence Score Weight',
        info: 'Weight of raw candidate score in candidate confidence.',
      },
      {
        key: 'sh.scoring.confidenceShapeWeight',
        type: 'number',
        level: 'debug',
        label: 'Confidence Shape Weight',
        info: 'Weight of contour shape metrics in candidate confidence.',
      },
      {
        key: 'sh.scoring.confidenceFitWeight',
        type: 'number',
        level: 'debug',
        label: 'Confidence Fit Weight',
        info: 'Weight of raw/arc-fit quality in candidate confidence.',
      },
      {
        key: 'sh.scoring.rawFitConfidence',
        type: 'number',
        level: 'debug',
        label: 'Raw Fit Confidence',
        info: 'Fit-confidence fallback used by raw bottom-point candidates.',
      },
      {
        key: 'sh.scoring.arcFitFallbackConfidence',
        type: 'number',
        level: 'debug',
        label: 'Arc Fit Fallback Confidence',
        info: 'Fit-confidence fallback used when an arc-fit candidate has no residual.',
      },
      {
        key: 'sh.scoring.fitResidualConfidenceScale',
        type: 'number',
        level: 'debug',
        label: 'Fit Residual Scale',
        info: 'Scale converting arc-fit residual into confidence penalty.',
      },
      {
        key: 'sh.scoring.minFitConfidence',
        type: 'number',
        level: 'debug',
        label: 'Min Fit Confidence',
        info: 'Lowest fit-confidence value allowed after residual penalty.',
      },
    ],
  },
  {
    id: 'coreMlQualityScoring',
    title: 'SH Quality Scoring',
    defaultOpen: true,
    items: [
      {
        key: 'sh.scoring.acceptanceQualityThreshold',
        type: 'number',
        label: 'Min SH Quality',
        info: 'Minimum final quality required before an SH candidate frame can be accepted.',
      },
      {
        key: 'sh.scoring.qualityPoseToleranceDeg',
        type: 'number',
        label: 'Quality Pose Tolerance',
        info: 'Pose-error range, in degrees, used when scoring SH candidate quality. Higher values are more forgiving.',
      },
      {
        key: 'sh.scoring.qualityDistanceToleranceCm',
        type: 'number',
        label: 'Quality Distance Tolerance',
        info: 'Distance range, in centimeters from the ideal distance, used when scoring SH candidate quality. Ideal is the midpoint of the native distance gate range.',
      },
      {
        key: 'sh.scoring.qualityScaleToleranceDeviation',
        type: 'number',
        label: 'Quality Scale Tolerance',
        info: 'VBOX-vs-PD scale disagreement at which the scale quality score reaches zero. Independent of the sh.scale bands: a disagreeing frame keeps using the VBOX scale but scores lower. Use 0 to drop the component.',
      },
      {
        key: 'sh.scoring.qualityRejectedCap',
        type: 'number',
        label: 'Rejected Quality Cap',
        info: 'Maximum quality reported for candidates that fail plausibility or contour-quality checks.',
      },
      {
        key: 'sh.scoring.qualityWeights.contour',
        type: 'number',
        level: 'dev',
        label: 'Contour Quality Weight',
        info: 'Emphasizes how convincing the selected lower-rim contour is, including support points, shape, lateral alignment, and vertical span.',
      },
      {
        key: 'sh.scoring.qualityWeights.symmetry',
        type: 'number',
        level: 'dev',
        label: 'Symmetry Quality Weight',
        info: 'Emphasizes agreement between left and right SH. Higher values penalize uneven measurements more strongly.',
      },
      {
        key: 'sh.scoring.qualityWeights.pose',
        type: 'number',
        level: 'dev',
        label: 'Pose Quality Weight',
        info: 'Emphasizes straight head pose. Higher values penalize candidates captured with stronger head rotation or tilt.',
      },
      {
        key: 'sh.scoring.qualityWeights.scale',
        type: 'number',
        level: 'dev',
        label: 'Scale Quality Weight',
        info: 'Weight of VBOX-vs-PD scale agreement in candidate quality. Only participates when a VBOX reference and detected spans exist; otherwise the blend renormalizes without it.',
      },
      {
        key: 'sh.scoring.qualityWeights.distance',
        type: 'number',
        level: 'dev',
        label: 'Distance Quality Weight',
        info: 'Emphasizes capture distance near the target range. Higher values penalize candidates taken too close or too far away.',
      },
      {
        key: 'sh.scoring.qualityWeights.estimate',
        type: 'number',
        level: 'dev',
        label: 'Estimate Quality Weight',
        info: 'Emphasizes agreement with the VBOX-based expected SH. Only applies when a formula and VBOX are available.',
        visibleWhen: (d) => d.sh.estimate.formula !== 'off',
      },
      {
        key: 'sh.scoring.qualityLateralScoreFactor',
        type: 'number',
        label: 'Lateral Score Factor',
        info: 'Eye-distance factor used to convert lateral drift into a candidate-quality penalty.',
      },
      {
        key: 'sh.scoring.qualityBottomClusterMinCount',
        type: 'number',
        integer: true,
        label: 'Bottom Cluster Min Count',
        info: 'Minimum number of lower-rim cluster points required for the candidate to pass quality checks.',
      },
      {
        key: 'sh.scoring.qualityBottomClusterWidthMinFactor',
        type: 'number',
        label: 'Bottom Cluster Min Width',
        info: 'Minimum lower-rim cluster width as a fraction of eye distance.',
      },
      {
        key: 'sh.scoring.qualityLateralDriftMaxFactor',
        type: 'number',
        label: 'Lateral Drift Max',
        info: 'Maximum allowed side drift from the pupil column as a fraction of eye distance.',
      },
      {
        key: 'sh.scoring.qualityTinyWidthMinFactor',
        type: 'number',
        label: 'Tiny Width Min',
        info: 'Minimum contour width as a fraction of eye distance before it is treated as too small.',
      },
      {
        key: 'sh.scoring.qualityWidthIdealMinFactor',
        type: 'number',
        label: 'Width Ideal Min',
        info: 'Lower bound of the full-score contour-width band, as a fraction of eye distance.',
      },
      {
        key: 'sh.scoring.qualityWidthIdealMaxFactor',
        type: 'number',
        label: 'Width Ideal Max',
        info: 'Upper bound of the full-score contour-width band, as a fraction of eye distance.',
      },
      {
        key: 'sh.scoring.qualityTinyHeightMinFactor',
        type: 'number',
        label: 'Tiny Height Min',
        info: 'Minimum contour height as a fraction of eye distance before it is treated as too small.',
      },
      {
        key: 'sh.scoring.qualityHeightIdealMinFactor',
        type: 'number',
        label: 'Height Ideal Min',
        info: 'Lower bound of the full-score contour-height band, as a fraction of eye distance.',
      },
      {
        key: 'sh.scoring.qualityHeightIdealMaxFactor',
        type: 'number',
        label: 'Height Ideal Max',
        info: 'Upper bound of the full-score contour-height band, as a fraction of eye distance.',
      },
      {
        key: 'sh.scoring.qualityHugeWidthMaxFactor',
        type: 'number',
        label: 'Huge Width Max',
        info: 'Maximum contour width as a fraction of eye distance before it is treated as too large.',
      },
      {
        key: 'sh.scoring.qualityHugeHeightMaxFactor',
        type: 'number',
        label: 'Huge Height Max',
        info: 'Maximum contour height as a fraction of eye distance before it is treated as too large.',
      },
      {
        key: 'sh.scoring.qualityVerticalSpanMinFactor',
        type: 'number',
        label: 'Vertical Span Min',
        info: 'Minimum top-to-bottom contour span as a fraction of eye distance.',
      },
      {
        key: 'sh.scoring.qualityVerticalSpanScoreMinFactor',
        type: 'number',
        label: 'Span Score Min',
        info: 'Lower bound where vertical-span score starts rising from zero.',
      },
      {
        key: 'sh.scoring.qualityVerticalSpanIdealMinFactor',
        type: 'number',
        label: 'Span Ideal Min',
        info: 'Lower bound of the full-score vertical-span band.',
      },
      {
        key: 'sh.scoring.qualityVerticalSpanIdealMaxFactor',
        type: 'number',
        label: 'Span Ideal Max',
        info: 'Upper bound of the full-score vertical-span band.',
      },
      {
        key: 'sh.scoring.qualityVerticalSpanScoreMaxFactor',
        type: 'number',
        label: 'Span Score Max',
        info: 'Upper bound where vertical-span score falls back to zero.',
      },
    ],
  },
  {
    id: 'visionCalibrationScoring',
    title: 'Vision Calibration Scoring',
    defaultOpen: false,
    items: [
      {
        key: 'sh.scoring.calibrationAspectRatioThreshold',
        type: 'number',
        level: 'debug',
        label: 'Calibration Aspect Threshold',
        info: 'Aspect ratio required for the calibration aspect bonus.',
      },
      {
        key: 'sh.scoring.calibrationAspectBonus',
        type: 'number',
        level: 'debug',
        label: 'Calibration Aspect Bonus',
        info: 'Calibration score added for wide, shallow rim-like contours.',
      },
      {
        key: 'sh.scoring.calibrationSpanMin',
        type: 'number',
        level: 'debug',
        label: 'Calibration Span Min',
        info: 'Minimum per-eye span fraction for calibration span bonus.',
      },
      {
        key: 'sh.scoring.calibrationSpanMax',
        type: 'number',
        level: 'debug',
        label: 'Calibration Span Max',
        info: 'Maximum per-eye span fraction for calibration span bonus.',
      },
      {
        key: 'sh.scoring.calibrationSpanBonus',
        type: 'number',
        level: 'debug',
        label: 'Calibration Span Bonus',
        info: 'Calibration score added when candidate span is in range.',
      },
      {
        key: 'sh.scoring.calibrationShapeWeight',
        type: 'number',
        level: 'debug',
        label: 'Calibration Shape Weight',
        info: 'Calibration score weight for smoothness and solidity.',
      },
      {
        key: 'sh.scoring.calibrationCompactnessWeight',
        type: 'number',
        level: 'debug',
        label: 'Calibration Compactness Weight',
        info: 'Calibration score weight for compactness.',
      },
      {
        key: 'sh.scoring.calibrationBothEyesBonus',
        type: 'number',
        level: 'debug',
        label: 'Calibration Both-Eyes Bonus',
        info: 'Maximum calibration bonus when both eyes have usable rim candidates.',
      },
      {
        key: 'sh.scoring.calibrationBothEyesQualityScale',
        type: 'number',
        level: 'debug',
        label: 'Calibration Both-Eyes Scale',
        info: 'Quality scale used to cap the both-eyes calibration bonus.',
      },
      {
        key: 'sh.scoring.calibrationSymmetryToleranceFactor',
        type: 'number',
        level: 'debug',
        label: 'Calibration Symmetry Tolerance',
        info: 'SH symmetry tolerance, as eye-separation fraction, for calibration scoring.',
      },
      {
        key: 'sh.scoring.calibrationSymmetryBonus',
        type: 'number',
        level: 'debug',
        label: 'Calibration Symmetry Bonus',
        info: 'Calibration score added when left/right rim bottoms are symmetric.',
      },
      {
        key: 'sh.scoring.calibrationOtherContourPenalty',
        type: 'number',
        level: 'debug',
        label: 'Calibration Other Penalty',
        info: 'Calibration score penalty per competing non-winning contour.',
      },
    ],
  },
  {
    id: 'draw',
    title: 'Draw Output',
    defaultOpen: false,
    items: [
      {
        key: 'draw.debugShapesEnabled',
        type: 'bool',
        level: 'debug',
        fallback: true,
        label: 'Debug Shapes',
        info: 'Draws development-only shapes such as ROI boxes, raw ARKit points, rejected Vision pupils, and non-winning contours.',
      },
      {
        key: 'draw.shQualityBoundsGuideEnabled',
        type: 'bool',
        fallback: false,
        label: 'SH Bounds Guide',
        info: 'Draws the selected SH contour bounding box.',
      },
      {
        key: 'draw.shQualityHeightGuideEnabled',
        type: 'bool',
        fallback: false,
        label: 'SH Height Guide',
        info: 'Draws the contour-height min, ideal, max, and actual ratio.',
      },
      {
        key: 'draw.shQualitySpanGuideEnabled',
        type: 'bool',
        fallback: false,
        label: 'SH Span Guide',
        info: 'Draws the top-to-bottom contour span min, ideal, max, and actual ratio.',
      },
      {
        key: 'draw.shQualityLateralGuideEnabled',
        type: 'bool',
        fallback: false,
        label: 'SH Lateral Guide',
        info: 'Draws the lateral drift acceptance band around the pupil column.',
      },
      {
        key: 'draw.shQualityEyeDistanceGuideEnabled',
        type: 'bool',
        fallback: false,
        label: 'SH Eye-Distance Guide',
        info: 'Draws the eye-distance ruler used as the scale reference for SH quality scores.',
      },
    ],
  },
] as const satisfies readonly SectionConfig[]

export const getSetting = (
  settings: OpticalMeasureSettings,
  path: OpticalMeasureSettingsKeys
) => resolvePath(settings, path)

export const setSetting = (
  settings: OpticalMeasureSettings,
  path: OpticalMeasureSettingsKeys,
  value: unknown
): OpticalMeasureSettings => {
  const keys = path.split('.')
  const setAt = (source: unknown, index: number): unknown => {
    const sourceObject =
      source && typeof source === 'object'
        ? (source as Record<string, unknown>)
        : {}
    const key = keys[index]

    if (index === keys.length - 1) {
      return { ...sourceObject, [key]: value }
    }

    return {
      ...sourceObject,
      [key]: setAt(sourceObject[key], index + 1),
    }
  }

  return setAt(settings, 0) as OpticalMeasureSettings
}
